<script>
  import { onMount } from 'svelte';
  import { user } from '$stores/sessionStore';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  // QA-012: AWS SDK removed; preview URLs flow through backend FileProxyController.

  const API_BASE_URL = '/api/proxy';

  let assignments = [];
  let loading = true;
  let error = null;
  let selectedAssignment = null;
  let loadingAssignment = false;
  let statusFilter = '';

  // Preview state
  let showPreview = false;
  let previewUrl = '';
  let previewType = '';
  let previewAssignmentId = null;
  let previewTaskId = null;

  let currentPage = 1;
  let totalPages = 1;
  let totalItems = 0;
  const pageSize = 50;

  // QA-012: bucket + credentials are server-side; SPA uses the backend proxy.

  const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.wmv'];
  const PDF_EXTENSIONS = ['.pdf'];
  const OFFICE_EXTENSIONS = {
    EXCEL: ['.xlsx', '.xls', '.xlsm', '.xlsb'],
    POWERPOINT: ['.pptx', '.ppt', '.pps', '.ppsx']
  };

  function isMediaFile(filename) {
    const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
    return {
      isImage: IMAGE_EXTENSIONS.includes(ext),
      isVideo: VIDEO_EXTENSIONS.includes(ext),
      isPdf: PDF_EXTENSIONS.includes(ext),
      isExcel: OFFICE_EXTENSIONS.EXCEL.includes(ext),
      isPowerPoint: OFFICE_EXTENSIONS.POWERPOINT.includes(ext)
    };
  }

  async function fetchAssignments() {
    try {
      loading = true;
      error = null;
      const params = new URLSearchParams({ page: String(currentPage), pageSize: String(pageSize) });
      // If we had the DB user id, we would pass it. Leaving server-side filtering for now.
      const resp = await fetch(`${API_BASE_URL}/userWorkflows?${params.toString()}`);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Failed to load your workflows');
      let items = data?.data || [];
      // Compute percent with populated workflow if available
      items = items.map(a => {
        const total = a?.workflowId?.tasks?.length || a?.taskStatuses?.length || 0;
        const done = (a?.taskStatuses || []).filter(t => !!t.confirmedAt).length;
        return { ...a, _progressPct: total > 0 ? Math.round((done / total) * 100) : 0 };
      });
      if (statusFilter) items = items.filter(a => a.status === statusFilter);
      assignments = items;
      totalItems = data?.pagination?.total || items.length;
      totalPages = data?.pagination?.totalPages || 1;
    } catch (e) {
      console.error('fetchAssignments error:', e);
      error = e.message;
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    fetchAssignments();
  });

  function handleRefresh() {
    fetchAssignments();
  }

  function handleFilterChange() {
    currentPage = 1;
    fetchAssignments();
  }

  function handlePageChange(p) {
    currentPage = p;
    fetchAssignments();
  }

  async function openAssignment(assignment) {
    try {
      loadingAssignment = true;
      // Fetch full assignment including populated workflow tasks
      const resp = await fetch(`${API_BASE_URL}/userWorkflows/${assignment._id}`);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Failed to load assignment');
      selectedAssignment = data;
    } catch (e) {
      console.error('openAssignment error:', e);
      alert(e.message || 'Failed to open assignment');
      selectedAssignment = assignment; // fallback to shallow data
    } finally {
      loadingAssignment = false;
    }
  }

  function closeAssignment() {
    selectedAssignment = null;
  }

  function closePreview() {
    showPreview = false;
    previewUrl = '';
    previewType = '';
    previewAssignmentId = null;
    previewTaskId = null;
  }

  async function openTaskPreview(assignment, task) {
    try {
      // Mark viewed automatically when opening
      await confirmTask(assignment._id, task._id, 'view');

      // QA-012: backend-proxied download URL (no browser-direct S3).
      const params = new URLSearchParams({ key: task.s3Key, filename: task.s3Key.split('/').pop() });
      const proxyUrl = `/api/proxy/files/download?${params.toString()}`;
      const filename = task.s3Key.split('/').pop();
      const { isImage, isVideo, isPdf, isExcel, isPowerPoint } = isMediaFile(filename);
      if (isExcel || isPowerPoint) {
        const fullUrl = new URL(proxyUrl, window.location.origin).toString();
        previewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fullUrl)}`;
        previewType = isExcel ? 'excel' : 'powerpoint';
      } else {
        previewUrl = proxyUrl;
        previewType = isImage ? 'image' : isVideo ? 'video' : isPdf ? 'pdf' : 'other';
      }
      previewAssignmentId = assignment._id;
      previewTaskId = task._id;
      showPreview = true;
    } catch (e) {
      console.error('openTaskPreview error:', e);
      alert(e.message || 'Failed to preview file');
    }
  }

  async function confirmTask(assignmentId, taskId, action) {
    try {
      const resp = await fetch(`${API_BASE_URL}/userWorkflows/${assignmentId}/tasks/${taskId}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Failed to update task');

      // Re-fetch populated assignment so tasks and workflow are available
      const populatedResp = await fetch(`${API_BASE_URL}/userWorkflows/${assignmentId}`);
      const populated = await populatedResp.json();
      if (!populatedResp.ok) throw new Error(populated?.message || 'Failed to refresh assignment');

      // Update list and modal with populated version
      assignments = assignments.map(a => a._id === assignmentId ? populated : a);
      if (selectedAssignment?._id === assignmentId) selectedAssignment = populated;
    } catch (e) {
      console.error('confirmTask error:', e);
      alert(e.message || 'Failed to update task');
    }
  }

  function percentComplete(a) {
    if (typeof a?._progressPct === 'number') return a._progressPct;
    const total = a?.workflowId?.tasks?.length || a?.taskStatuses?.length || 0;
    if (!total) return 0;
    const done = (a.taskStatuses || []).filter(t => !!t.confirmedAt).length;
    return Math.round((done / total) * 100);
  }

  function actionLabel(a) {
    const pct = percentComplete(a);
    if (pct <= 0) return 'Start';
    if (pct >= 100) return 'View';
    return 'Continue';
  }
</script>

<FeatureInfo title="My Workflows" description="View and complete your assigned workflows." marginBottom="mb-0" />

<Toolbar title="My Workflows" itemCountTitle="Workflows" itemCount={totalItems}>
  <div class="flex items-center gap-4">
    <div class="text-m mb-0">
      Status
      <select
        bind:value={statusFilter}
        on:change={handleFilterChange}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      >
        <option value="">All</option>
        <option value="assigned">Assigned</option>
        <option value="in_progress">In progress</option>
        <option value="completed">Completed</option>
      </select>
    </div>

    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if error}
  <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mx-4">{error}</div>
{/if}

{#if loading}
  <div class="flex justify-center items-center py-4">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Workflow</th>
          <th class="px-4 py-2 text-left bold">Version</th>
          <th class="px-4 py-2 text-left bold">Required</th>
          <th class="px-4 py-2 text-left bold">Progress</th>
          <th class="px-4 py-2 text-left bold">Assigned</th>
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="w-8 px-4 py-2 text-left bold">&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {#each assignments as a, index}
          <tr class="text-sm text-gray-800 dark:text-slate-300 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}">
            <td class="px-4 py-2">
              <div class="font-medium">{a.workflowId?.name || 'Workflow'}</div>
              {#if a.workflowId?.description}
                <div class="text-xs text-gray-500 dark:text-gray-400">{a.workflowId.description}</div>
              {/if}
            </td>
            <td class="px-4 py-2">{a?.workflowId?.version || ''}</td>
            <td class="px-4 py-2">{a?.workflowId?.required ? 'Yes' : 'No'}</td>
            <td class="px-4 py-2">{percentComplete(a)}%</td>
            <td class="px-4 py-2">{a.assignedAt ? new Date(a.assignedAt).toLocaleString() : ''}</td>
            <td class="px-4 py-2 capitalize">{a.status?.replace('_', ' ') || 'assigned'}</td>
            <td class="px-4 py-2">
              <button type="button" class="px-3 py-1 text-sm border rounded dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600" on:click={() => openAssignment(a)}>{actionLabel(a)}</button>
            </td>
          </tr>
        {/each}
        {#if (assignments || []).length === 0}
          <tr>
            <td colspan="4" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">No workflows assigned.</td>
          </tr>
        {/if}
      </tbody>
    </table>

    {#if totalPages > 1}
      <div class="flex justify-center items-center py-2">
        <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
      </div>
    {/if}
  </div>
{/if}

{#if selectedAssignment}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={closeAssignment}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-4xl relative z-60 overflow-y-auto max-h-[90vh]" on:click|stopPropagation>
      <header class="flex justify-between items-start mb-3 text-gray-900 dark:text-gray-100">
        <h2 class="text-xl font-bold">{selectedAssignment.workflowId?.name}</h2>
        <button class="text-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200" on:click={closeAssignment}>&times;</button>
      </header>

      {#if selectedAssignment.workflowId?.description}
        <div class="text-sm text-gray-700 dark:text-gray-300 mb-3">{selectedAssignment.workflowId.description}</div>
      {/if}

      <div class="space-y-3">
        {#if loadingAssignment}
          <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            Loading tasks…
          </div>
        {/if}
        {#each selectedAssignment.workflowId?.tasks || [] as t, idx}
          {@const status = selectedAssignment.taskStatuses?.find(s => String(s.taskId) === String(t._id))}
          <div class="p-3 rounded border dark:border-gray-700 bg-stone-50 dark:bg-gray-800">
            <div class="flex items-center justify-between">
              <div>
                <div class="font-medium text-gray-900 dark:text-gray-100">{idx + 1}. {t.title}</div>
                {#if t.description}
                  <div class="text-xs text-gray-600 dark:text-gray-300">{t.description}</div>
                {/if}
                
              </div>
              <div class="flex items-center gap-2">
                <button type="button" class="px-3 py-1 text-sm border rounded dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600" on:click={() => openTaskPreview(selectedAssignment, t)}>View</button>
              </div>
            </div>
            <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {#if status?.viewedAt}
                Viewed: {new Date(status.viewedAt).toLocaleString()}
              {/if}
              {#if status?.confirmedAt}
                • Confirmed: {new Date(status.confirmedAt).toLocaleString()}
              {/if}
            </div>
          </div>
        {/each}
      </div>
      <div class="flex justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-gray-700 mt-4">
        <button type="button" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700" on:click={closeAssignment}>Close</button>
      </div>
    </div>
  </div>
{/if}

{#if showPreview}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <button type="button" class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={closePreview} on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') closePreview(); }} aria-label="Close preview"></button>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-5xl relative z-60 overflow-y-auto max-h-[90vh]" role="dialog" aria-modal="true" on:click|stopPropagation>
      <header class="flex justify-between items-start mb-3 text-gray-900 dark:text-gray-100">
        <h2 class="text-xl font-bold">Preview</h2>
        <button class="text-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200" on:click={closePreview}>&times;</button>
      </header>
      {#if previewType === 'image'}
        <img src={previewUrl} alt="Preview" class="max-w-full max-h-[75vh] object-contain" />
      {:else if previewType === 'video'}
        <!-- svelte-ignore a11y-media-has-caption -->
        <video src={previewUrl} controls class="w-full max-h-[75vh]">Your browser does not support the video tag.</video>
      {:else if previewType === 'pdf'}
        <div class="flex justify-center w-full">
          <iframe src={previewUrl} class="w-[90vw] h-[80vh] border-0 bg-white" title="PDF Preview"></iframe>
        </div>
      {:else if previewType === 'excel' || previewType === 'powerpoint'}
        <div class="flex justify-center w-full">
          <iframe src={previewUrl} class="w-[90vw] h-[80vh] border-0 bg-white" title={`${previewType === 'excel' ? 'Excel' : 'PowerPoint'} Preview`} frameborder="0" allowfullscreen="true"></iframe>
        </div>
      {:else}
        <div class="text-sm text-gray-700 dark:text-gray-300">File type not previewable. <a href={previewUrl} target="_blank" class="text-blue-600 underline">Open in new tab</a></div>
      {/if}
      <div class="flex justify-end space-x-2 pt-3 border-t border-gray-200 dark:border-gray-700 mt-4">
        <button type="button" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700" on:click={closePreview}>Close</button>
        {#if previewAssignmentId && previewTaskId}
          <button type="button" class="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700" on:click={() => { confirmTask(previewAssignmentId, previewTaskId, 'confirm'); closePreview(); }}>Completed</button>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .z-60 { z-index: 60; }
</style>


